<template>
  <section class="audit-form-page">
    <div class="container">
      <router-link to="/" class="back-link">← {{ $t('auditForm.backToHome') }}</router-link>
      
      <div class="form-header">
        <h1>{{ $t('auditForm.title') }}</h1>
        <p>{{ $t('auditForm.subtitle') }}</p>
      </div>

      <div class="page-layout">
        <div class="form-container">
          <!-- Using Formspree as the proposed submission method -->
          <form @submit.prevent="handleSubmit" class="audit-form">
            <div class="form-group">
              <label for="name">{{ $t('auditForm.labels.name') }}*</label>
              <input type="text" id="name" v-model="form.name" required />
            </div>

            <div class="form-group">
              <label for="email">{{ $t('auditForm.labels.email') }}*</label>
              <input type="email" id="email" v-model="form.email" required />
            </div>

            <div class="form-group">
              <label for="company">{{ $t('auditForm.labels.company') }}</label>
              <input type="text" id="company" v-model="form.company" />
            </div>

            <div class="form-group">
              <label for="industry">{{ $t('auditForm.labels.industry') }}</label>
              <select id="industry" v-model="form.industry">
                <option value="" disabled>{{ $t('auditForm.placeholders.selectIndustry') }}</option>
                <option v-for="opt in tm('auditForm.options.industries')" :key="opt" :value="rt(opt)">
                  {{ rt(opt) }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>{{ $t('auditForm.labels.dataMaturity') }}</label>
              <div class="radio-group">
                <label v-for="(label, key) in tm('auditForm.options.maturityLevels')" :key="key" class="radio-label">
                  <input type="radio" v-model="form.dataMaturity" :value="key" />
                  <span>{{ rt(label) }}</span>
                </label>
              </div>
            </div>

            <!-- 2026 GenAI Readiness Checklist Sections -->
            <div v-for="(section, sectionKey) in tm('auditForm.sections')" :key="sectionKey" class="checklist-section">
              <h3>{{ rt(section.title) }}</h3>
              <p class="section-subtitle">{{ rt(section.subtitle) }}</p>
              <div class="checkbox-group">
                <label v-for="(item, itemKey) in section.items" :key="itemKey" class="checkbox-label">
                  <input type="checkbox" v-model="form.checklist[sectionKey][itemKey]" />
                  <span>{{ rt(item) }}</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label for="challenges">{{ $t('auditForm.labels.challenges') }}</label>
              <textarea id="challenges" v-model="form.challenges" rows="4"></textarea>
            </div>

            <div class="form-group">
              <label for="goals">{{ $t('auditForm.labels.goals') }}</label>
              <textarea id="goals" v-model="form.goals" rows="4"></textarea>
            </div>

            <div class="confidential-note">
              <span>🔒 {{ $t('auditForm.confidentialNote') }}</span>
              <router-link to="/share" target="_blank" class="confidential-link">
                {{ $t('auditForm.confidentialLink') }} →
              </router-link>
            </div>

            <button type="submit" class="submit-button" :disabled="submitting">
              <span v-if="!submitting">{{ $t('auditForm.submit') }}</span>
              <span v-else>{{ $t('auditForm.sending') }}</span>
            </button>

            <p v-if="success" class="success-message">{{ $t('auditForm.success') }}</p>
            <p v-if="error" class="error-message">{{ error }}</p>
          </form>
        </div>

        <!-- Social Proof Sidebar -->
        <aside class="social-proof-sidebar">
          <div class="sidebar-card">
            <div class="framework-badge">
              <span class="badge-icon">🛡️</span>
              <span class="badge-text">{{ $t('auditForm.socialProof.frameworkBadge') }}</span>
            </div>

            <h3>{{ $t('auditForm.socialProof.title') }}</h3>
            <p class="framework-description">{{ $t('auditForm.socialProof.frameworkDescription') }}</p>

            <div class="stats-list">
              <div class="stat-item">
                <span class="stat-icon">✅</span>
                <span>{{ $t('auditForm.socialProof.stats.auditsCompleted') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-icon">📊</span>
                <span>{{ $t('auditForm.socialProof.stats.complianceRate') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-icon">🏢</span>
                <span>{{ $t('auditForm.socialProof.stats.industries') }}</span>
              </div>
            </div>

            <blockquote class="testimonial">
              <p>{{ $t('auditForm.socialProof.testimonial') }}</p>
              <cite>{{ $t('auditForm.socialProof.testimonialAuthor') }}</cite>
            </blockquote>
          </div>

          <div class="sidebar-card credential-safety-card">
            <div class="framework-badge security-badge">
              <span class="badge-icon">🔒</span>
              <span class="badge-text">{{ $t('auditForm.secretShare.badge') }}</span>
            </div>

            <h3>{{ $t('auditForm.secretShare.title') }}</h3>
            <p class="framework-description">{{ $t('auditForm.secretShare.description') }}</p>

            <router-link to="/share" target="_blank" class="sidebar-secret-btn">
              {{ $t('auditForm.secretShare.cta') }} →
            </router-link>
          </div>
        </aside>
      </div>
    </div>

    <!-- Exit Intent Popup -->
    <Transition name="fade">
      <div v-if="showExitPopup" class="exit-popup-overlay" @click.self="dismissPopup">
        <div class="exit-popup">
          <button class="popup-close" @click="dismissPopup">✕</button>
          <h2>{{ $t('auditForm.exitIntent.title') }}</h2>
          <p>{{ $t('auditForm.exitIntent.subtitle') }}</p>

          <form v-if="!checklistSuccess" @submit.prevent="handleChecklistDownload" class="checklist-form">
            <input
              type="email"
              v-model="checklistEmail"
              :placeholder="$t('auditForm.exitIntent.emailPlaceholder')"
              required
              class="checklist-email-input"
            />
            <button type="submit" class="checklist-cta">{{ $t('auditForm.exitIntent.cta') }}</button>
          </form>
          <p v-else class="checklist-success">{{ $t('auditForm.exitIntent.success') }}</p>

          <button class="dismiss-link" @click="dismissPopup">{{ $t('auditForm.exitIntent.dismiss') }}</button>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { db } from '@/firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const { tm, rt } = useI18n()

const form = reactive({
  name: '',
  email: '',
  company: '',
  industry: '',
  dataMaturity: '',
  checklist: {
    foundation: {
      unifiedAccess: false,
      latencyBenchmarking: false,
      dataVersioning: false
    },
    architecture: {
      deterministicGuardrails: false,
      toolUse: false,
      orchestrationLayer: false
    },
    mlops: {
      evalFrameworks: false,
      traceability: false,
      costControl: false
    },
    privacy: {
      gdprScrubbing: false,
      privateCloud: false,
      hitl: false
    }
  },
  challenges: '',
  goals: ''
})

const submitting = ref(false)
const success = ref(false)
const error = ref(null)

const handleSubmit = async () => {
  submitting.value = true
  error.value = null
  
  try {
    await addDoc(collection(db, 'audit_requests'), {
      ...form,
      timestamp: serverTimestamp()
    })

    success.value = true
    form.name = ''
    form.email = ''
    form.company = ''
    form.industry = ''
    form.dataMaturity = ''
    form.challenges = ''
    form.goals = ''
    // Reset checklist
    Object.keys(form.checklist).forEach(sectionKey => {
      Object.keys(form.checklist[sectionKey]).forEach(itemKey => {
        form.checklist[sectionKey][itemKey] = false
      })
    })
  } catch (err) {
    console.error('Audit submission error:', err)
    error.value = 'Something went wrong. Please try again or contact us directly at kontakt@data-do.de'
  } finally {
    submitting.value = false
  }
}

// Exit Intent Popup Logic
const showExitPopup = ref(false)
const exitPopupShown = ref(false)
const checklistEmail = ref('')
const checklistSuccess = ref(false)
const formInteracted = ref(false)

const trackInteraction = () => {
  formInteracted.value = true
}

const handleMouseLeave = (e) => {
  if (e.clientY <= 0 && formInteracted.value && !exitPopupShown.value && !success.value) {
    showExitPopup.value = true
    exitPopupShown.value = true
  }
}

const dismissPopup = () => {
  showExitPopup.value = false
}

const handleChecklistDownload = async () => {
  try {
    await addDoc(collection(db, 'checklist_downloads'), {
      email: checklistEmail.value,
      timestamp: serverTimestamp()
    })
    checklistSuccess.value = true
  } catch (err) {
    console.error('Checklist download error:', err)
  }
}

onMounted(() => {
  document.addEventListener('mouseleave', handleMouseLeave)
  const formEl = document.querySelector('.audit-form')
  if (formEl) {
    formEl.addEventListener('input', trackInteraction)
  }
})

onUnmounted(() => {
  document.removeEventListener('mouseleave', handleMouseLeave)
  const formEl = document.querySelector('.audit-form')
  if (formEl) {
    formEl.removeEventListener('input', trackInteraction)
  }
})
</script>

<style scoped>
.audit-form-page {
  padding: 60px 20px;
  background-color: var(--white);
  min-height: 80vh;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
}

.back-link {
  display: inline-block;
  margin-bottom: 30px;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
}

.form-header {
  margin-bottom: 40px;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  color: var(--text-color);
}

.form-header p {
  font-size: 1.2rem;
  opacity: 0.8;
}

.page-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 40px;
  align-items: start;
}

.form-container {
  background-color: var(--gray-light);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
}

.audit-form {
  display: grid;
  gap: 20px;
}

.form-group {
  display: grid;
  gap: 8px;
}

label {
  font-weight: 600;
  color: var(--text-color);
}

input, select, textarea {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
}

input:focus, select:focus, textarea:focus {
  border-color: var(--primary-color);
  outline: none;
}

.radio-group {
  display: grid;
  gap: 10px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: normal;
  cursor: pointer;
}

.checklist-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.checklist-section h3 {
  margin-bottom: 5px;
  color: var(--primary-color);
  font-size: 1.2rem;
}

.section-subtitle {
  font-style: italic;
  font-size: 0.9rem;
  margin-bottom: 15px;
  opacity: 0.8;
}

.checkbox-group {
  display: grid;
  gap: 12px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-weight: normal;
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1.4;
}

.checkbox-label input {
  margin-top: 4px;
}

.submit-button {
  background-color: var(--primary-color);
  color: white;
  padding: 15px;
  font-size: 1.1rem;
  font-weight: bold;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 10px;
}

.submit-button:hover {
  background-color: var(--primary-dark);
}

.submit-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.success-message {
  color: #2e7d32;
  font-weight: 600;
  text-align: center;
  margin-top: 10px;
}

.error-message {
  color: #c62828;
  font-weight: 600;
  text-align: center;
  margin-top: 10px;
}

.confidential-note {
  background-color: var(--primary-light);
  border: 1px solid rgba(0, 85, 255, 0.15);
  border-radius: 8px;
  padding: 14px 18px;
  font-size: 0.9rem;
  color: var(--text-color);
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  margin-bottom: 10px;
}

.confidential-link {
  color: var(--primary-color);
  font-weight: 700;
  text-decoration: none;
  font-size: 0.92rem;
}

.confidential-link:hover {
  text-decoration: underline;
}

/* Social Proof Sidebar */
.social-proof-sidebar {
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sidebar-card {
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%);
  border: 1px solid rgba(21, 91, 213, 0.15);
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
}

.credential-safety-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(2, 132, 199, 0.2);
}

.security-badge {
  background-color: #0284c7;
}

.sidebar-secret-btn {
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s, transform 0.2s;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}

.sidebar-secret-btn:hover {
  background-color: var(--primary-dark);
  transform: translateY(-1px);
}

.framework-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--primary-color);
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 20px;
  line-height: 1.3;
}

.badge-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.sidebar-card h3 {
  font-size: 1.25rem;
  color: var(--text-color);
  margin-bottom: 12px;
}

.framework-description {
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 0.85;
  margin-bottom: 20px;
}

.stats-list {
  display: grid;
  gap: 10px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color);
}

.stat-icon {
  font-size: 1.1rem;
}

.testimonial {
  border-left: 3px solid var(--primary-color);
  padding-left: 14px;
  margin: 0;
}

.testimonial p {
  font-size: 0.88rem;
  font-style: italic;
  line-height: 1.5;
  margin-bottom: 8px;
  color: var(--text-color);
}

.testimonial cite {
  font-size: 0.8rem;
  opacity: 0.7;
  font-style: normal;
}

/* Exit Intent Popup */
.exit-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.exit-popup {
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 480px;
  width: 90%;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.popup-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  color: #999;
  padding: 4px;
}

.popup-close:hover {
  color: #333;
}

.exit-popup h2 {
  font-size: 1.6rem;
  color: var(--text-color);
  margin-bottom: 10px;
}

.exit-popup > p {
  font-size: 1.05rem;
  opacity: 0.8;
  margin-bottom: 24px;
}

.checklist-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.checklist-email-input {
  padding: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  text-align: center;
}

.checklist-email-input:focus {
  border-color: var(--primary-color);
  outline: none;
}

.checklist-cta {
  background-color: var(--primary-color);
  color: white;
  padding: 14px;
  font-size: 1.05rem;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.checklist-cta:hover {
  background-color: var(--primary-dark);
}

.checklist-success {
  color: #2e7d32;
  font-weight: 600;
  font-size: 1.05rem;
  margin-bottom: 16px;
}

.dismiss-link {
  background: none;
  border: none;
  color: #999;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.dismiss-link:hover {
  color: #666;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .page-layout {
    grid-template-columns: 1fr;
  }

  .social-proof-sidebar {
    position: static;
    order: -1;
  }
}

@media (max-width: 600px) {
  .form-container {
    padding: 20px;
  }

  .exit-popup {
    padding: 28px 20px;
  }
}
</style>
